import { useEffect, useMemo } from "react";
import { useURLTools } from "utils/URLtools";
import { useRegion_v2 } from "contexts/regions_v2";
import { REPORTS_PERIOD_DAYS } from "./api";
import { TReportFilters } from "./types";

const isoDate = (d: Date) => d.toISOString().split("T")[0];

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

/**
 * Report page filters stored in the URL (`from`, `to`, `provinceId`, `cityId`,
 * `jurisdictionId`, optionally `topN`) so pages are shareable and keep their
 * filters across refreshes. Region falls back to the globally selected region.
 */
export function useReportFilters(options?: { withTopN?: boolean }) {
  const { queryParams, addUpdateRemoveQueryParams } = useURLTools();
  const { selectedRegion } = useRegion_v2();
  const withTopN = !!options?.withTopN;

  // seed the default date range into the URL on first load
  useEffect(() => {
    if (queryParams && (!queryParams.from || !queryParams.to)) {
      addUpdateRemoveQueryParams([
        {
          parameter: "from",
          value: queryParams.from || isoDate(daysAgo(REPORTS_PERIOD_DAYS)),
        },
        { parameter: "to", value: queryParams.to || isoDate(new Date()) },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  /** false until the URL carries a date range (prevents a duplicate initial fetch) */
  const isReady = Boolean(queryParams?.from && queryParams?.to);

  const filters: TReportFilters = useMemo(
    () => ({
      startD: queryParams?.from || isoDate(daysAgo(REPORTS_PERIOD_DAYS)),
      endD: queryParams?.to || isoDate(new Date()),
      provinceId: queryParams?.provinceId ?? selectedRegion.province?.id,
      cityId: queryParams?.cityId ?? selectedRegion.city?.id,
      jurisdictionId:
        queryParams?.jurisdictionId ?? selectedRegion.jurisdiction?.id,
      ...(withTopN && { topN: Number(queryParams?.topN) || 10 }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryParams, selectedRegion, withTopN]
  );

  const applyFilters = (values: TReportFilters) => {
    addUpdateRemoveQueryParams([
      { parameter: "from", value: values.startD || "" },
      { parameter: "to", value: values.endD || "" },
      { parameter: "provinceId", value: values.provinceId || "" },
      { parameter: "cityId", value: values.cityId || "" },
      { parameter: "jurisdictionId", value: values.jurisdictionId || "" },
      ...(withTopN
        ? [{ parameter: "topN", value: String(values.topN || 10) }]
        : []),
    ]);
  };

  return { filters, applyFilters, isReady };
}

export default useReportFilters;
